import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Eye,
  X,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  Calendar,
  Search,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  User,
  Building2,
  Users,
  UserMinus,
  History,
  Wallet,
  Receipt,
  ChevronDown
} from 'lucide-react';
import payrollRunApi from '../../../api/payrollRunApi';
import Drawer from '../../../components/common/Drawer';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { formatDate } from '../../../utils/stringUtils';

const PayrollRunDetails = ({ runId }) => {
  const navigate = useNavigate();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const { data: runData, isLoading, error } = useQuery({
    queryKey: ['payrollRunDetails', runId],
    queryFn: () => payrollRunApi.getPayrollRunDetails(runId),
    enabled: !!runId,
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleViewEmployee = (employee) => {
    setSelectedEmployee(employee);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEmployee(null);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedEmployees(sortedEmployees.map(emp => emp.id));
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleSelectEmployee = (id) => {
    setSelectedEmployees(prev =>
      prev.includes(id)
        ? prev.filter(empId => empId !== id)
        : [...prev, id]
    );
  };

  const handleExport = () => {
    const employeesToExport = selectedEmployees.length > 0
      ? sortedEmployees.filter(emp => selectedEmployees.includes(emp.id))
      : sortedEmployees;

    if (employeesToExport.length === 0) return;

    const headers = ['Employee Name', 'Employee ID', 'Email', 'Department', 'Net Pay', 'Present Days', 'Status'];
    const csvData = employeesToExport.map(emp => [
      `"${emp.name}"`,
      `"${emp.id}"`,
      `"${emp.email}"`,
      `"${emp.department}"`,
      emp.netPay,
      emp.presentDays,
      emp.status
    ]);

    const csvContent = [headers, ...csvData].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `payroll_details_${details?.period || 'export'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  if (error || !runData) {
    return (
      <div className="text-center p-8 bg-red-50 text-red-600 rounded-lg">
        <AlertCircle className="h-8 w-8 mx-auto mb-2" />
        <p>Failed to load payroll run details.</p>
        <p className="text-sm mt-2">{error ? error.message || String(error) : "No data returned"}</p>
        <button onClick={handleBack} className="mt-4 text-brand hover:underline">
          Back to Payroll Runs
        </button>
      </div>
    );
  }

  const details = runData;
  const rawEmployees = details.employees || [];

  // Filter and Sort employees
  const filteredEmployees = rawEmployees.filter(emp =>
    (emp.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (emp.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (emp.department || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];

    if (typeof valA === 'string') {
      valA = valA.toLowerCase();
      valB = valB.toLowerCase();
    }

    if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
    if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const renderSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />;
    return sortOrder === 'asc' ?
      <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> :
      <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />;
  };

  return (
    <div className="space-y-6">
      {/* Header with back button, title, and search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                icon={<ArrowLeft className="h-4 w-4" />}
                onClick={handleBack}
              >
                Back
              </Button>
              <div className="flex flex-col">
                <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                  {details.payGroup} - {details.period}
                </h1>
                <p className="text-xs font-medium text-gray-500 mt-1 flex items-center">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"></span>
                  Payroll Period: {details.period}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <PermissionGate permission="payroll" action="read">
                <Button
                  variant="outline"
                  icon={<Download className="h-4 w-4" />}
                  onClick={handleExport}
                >
                  Export {selectedEmployees.length > 0 ? `(${selectedEmployees.length})` : ''}
                </Button>
              </PermissionGate>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-0 focus:border-brand"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Employee List Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-transparent" style={{ backgroundColor: "rgba(59, 130, 246, 0.14)" }}>
              <tr>
                <th className="px-6 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-brand focus:ring-0 cursor-pointer"
                    checked={sortedEmployees.length > 0 && selectedEmployees.length === sortedEmployees.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider max-w-xs cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Employee</span>
                    {renderSortIcon('name')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider max-w-[120px] cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('id')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Employee ID</span>
                    {renderSortIcon('id')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider max-w-[200px] cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('department')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Department</span>
                    {renderSortIcon('department')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('netPay')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Net Pay</span>
                    {renderSortIcon('netPay')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('presentDays')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Present Days</span>
                    {renderSortIcon('presentDays')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {renderSortIcon('status')}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedEmployees.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    No employees found
                  </td>
                </tr>
              ) : (
                sortedEmployees.map((emp) => (
                  <tr key={emp.id} className="hover:bg-blue-50/30">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-brand focus:ring-0 cursor-pointer"
                        checked={selectedEmployees.includes(emp.id)}
                        onChange={() => handleSelectEmployee(emp.id)}
                      />
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <div className="text-sm font-medium text-gray-900 truncate" title={emp.name}>
                        {emp.name}
                      </div>
                      <div className="text-sm text-gray-500 truncate" title={emp.email}>
                        {emp.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-[120px]">
                      <div className="text-sm text-gray-900 truncate" title={emp.id}>
                        {emp.id}
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-[200px]">
                      <div className="text-sm text-gray-900 truncate" title={emp.department}>
                        {emp.department}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ₹{(emp.netPay || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {emp.presentDays || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {emp.status === 'ready' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Ready
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Excluded
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-left">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => handleViewEmployee(emp)}
                          className="text-[#0F2942] hover:text-blue-800 transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Employee Details Drawer */}
      {selectedEmployee && (
        <Drawer
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title="Payroll Details"
          size="medium"
        >
          {selectedEmployee && (
            <div className="p-5 space-y-5 bg-white">
              {/* Header Section */}
              <div className="mb-4">
                <h2 className="text-lg font-bold text-gray-900 leading-tight">{selectedEmployee.name}</h2>
                <p className="text-[12px] text-gray-500">Employee ID: {selectedEmployee.id}</p>
              </div>

              {/* Main Info Cards */}
              <div className="grid grid-cols-2 gap-x-8 gap-y-4 p-5 bg-white rounded-xl border border-gray-100 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <User className="w-4 h-4 text-[#0F2942]" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Employee ID</p>
                    <p className="text-[13px] font-bold text-gray-900">{selectedEmployee.id}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <Building2 className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Department</p>
                    <p className="text-[13px] font-bold text-gray-900">{selectedEmployee.department || 'Not Assigned'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <Calendar className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Pay Period</p>
                    <p className="text-[13px] font-bold text-gray-900">{details?.period || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-teal-50 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Status</p>
                    <div className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100/60 text-green-700 border border-green-200">
                      Ready for Processing
                    </div>
                  </div>
                </div>
              </div>

              {/* Attendance Statistics */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center space-y-1">
                  <div className="p-2.5 bg-green-50 rounded-full mb-1">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase leading-tight">Present Days</p>
                  <p className="text-2xl font-bold text-green-600 leading-none">{selectedEmployee.presentDays || 0}</p>
                  <p className="text-[10px] text-gray-400 font-medium uppercase">Days</p>
                </div>
                <div className="p-3 bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center space-y-1">
                  <div className="p-2.5 bg-red-50 rounded-full mb-1">
                    <UserMinus className="w-5 h-5 text-red-600" />
                  </div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase leading-tight">Absent Days</p>
                  <p className="text-2xl font-bold text-red-600 leading-none">{selectedEmployee.totalLeave || 0}</p>
                  <p className="text-[10px] text-gray-400 font-medium uppercase">Days</p>
                </div>
                <div className="p-3 bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center space-y-1">
                  <div className="p-2.5 bg-blue-50 rounded-full mb-1">
                    <History className="w-5 h-5 text-[#0F2942]" />
                  </div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase leading-tight">Payroll Status</p>
                  <p className="text-xl font-bold text-[#0B2033] leading-none">Ready</p>
                  <p className="text-[10px] text-gray-400 font-medium uppercase">For Processing</p>
                </div>
              </div>

              {/* Earnings & Deductions Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* Earnings Card */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  <div className="bg-blue-50/40 px-4 py-2.5 border-b border-gray-100 flex items-center space-x-2">
                    <Wallet className="w-3.5 h-3.5 text-[#0F2942]" />
                    <h3 className="font-bold text-gray-900 text-[11px] uppercase tracking-wider">Earnings</h3>
                  </div>
                  <div className="p-4 space-y-3.5">
                    <div className="flex justify-between items-center text-[12px]">
                      <span className="text-gray-500 pr-2">Basic Salary (Annual) </span>
                      <span className="font-bold text-gray-900">₹{selectedEmployee.salaryCalculation?.basicSalary?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between items-center text-[12px]">
                      <span className="text-gray-500">Monthly Salary</span>
                      <span className="font-bold text-gray-900">₹{selectedEmployee.overallNetPay?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}</span>
                    </div>
                    <div className="pt-3.5 border-t border-dotted border-gray-200 flex justify-between items-center">
                      <span className="text-[12px] font-bold text-[#0B2033]">Gross Earnings (Annual)</span>
                      <span className="text-[13px] font-bold text-[#0B2033]">₹{((selectedEmployee.basicSalary || 0) + (selectedEmployee.allowances || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions Card */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  <div className="bg-red-50/40 px-4 py-2.5 border-b border-gray-100 flex items-center space-x-2">
                    <Receipt className="w-3.5 h-3.5 text-red-600" />
                    <h3 className="font-bold text-gray-900 text-[11px] uppercase tracking-wider">Deductions</h3>
                  </div>
                  <div className="p-4 space-y-3.5">
                    <div className="flex justify-between items-center text-[12px]">
                      <span className="text-gray-500">PF</span>
                      <span className="font-bold text-gray-900">₹{(selectedEmployee.salaryCalculation?.deductions?.pf || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between items-center text-[12px]">
                      <span className="text-gray-500">ESIC</span>
                      <span className="font-bold text-gray-900">₹{(selectedEmployee.salaryCalculation?.deductions?.esi || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between items-center text-[12px]">
                      <span className="text-gray-500">Tax</span>
                      <span className="font-bold text-gray-900">₹{(selectedEmployee.salaryCalculation?.deductions?.tds || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="pt-3.5 border-t border-dotted border-gray-200 flex justify-between items-center">
                      <span className="text-[12px] font-bold text-red-600">Total Deductions</span>
                      <span className="text-[13px] font-bold text-red-600">₹{selectedEmployee.deductions?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '0.00'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Pay Banner */}
              <div className="bg-blue-50/80 rounded-xl border border-blue-100 p-5 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-white rounded-xl shadow-sm border border-blue-50">
                    <Wallet className="w-7 h-7 text-[#0F2942]" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-[#0B2033] uppercase tracking-widest mb-1">Net Pay</p>
                    <p className="text-3xl font-bold text-blue-800 tracking-tight">₹{selectedEmployee.netPay?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 text-gray-400">
                  <div className="p-2 bg-white rounded-full shadow-sm border border-gray-50">
                    <Building2 className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="text-[10px] leading-tight">
                    <p className="font-medium">Credited to</p>
                    <p className="font-bold text-gray-600">Bank Account</p>
                  </div>
                </div>
              </div>


            </div>
          )}
        </Drawer>
      )}
    </div>
  );
};

export default PayrollRunDetails;
